import { GetAllRequest } from '@/types/search';
import api from './api';
import { BaseService, PaginatedResult } from './base.service';
import { CreateLocationDto, Location, LocationFacilityCalendar, LocationProductSummary, LocationRequest, LocationSetupStatus, LocationSnapshotItem, LocationSummaryDto, TrackmanCredentialsStatus, TrackmanSyncResultDto, UpdateTrackmanCredentialsRequest } from '@/types/location';
import { Plan } from '@/types/plan';
import { BookingType } from '@/types/booking-type';
import { PriceChangeRequest, PricingDto } from '@/types/pricing';

export class LocationService extends BaseService {
  constructor() {
    super('/location');
  }

  async getAllLocations(
    req: GetAllRequest
  ): Promise<PaginatedResult<Location>> {
    return this.post<PaginatedResult<Location>>('/all', req);
  }

  async getLocation(id: string): Promise<Location> {
    return this.get<Location>(`/${id}`);
  }

  async getSwitcherLocations(): Promise<LocationSummaryDto[]> {
    return this.get<LocationSummaryDto[]>('/switcher');
  }

  async getLocationsSnapshot(): Promise<LocationSnapshotItem[]> {
    // Base endpoint is '/location'; appending '-snapshot' forms '/location-snapshot'
    // which maps to the LocationSnapshotController at @Controller('location-snapshot')
    return this.get<LocationSnapshotItem[]>('-snapshot');
  }

  async createLocation(location: CreateLocationDto): Promise<Location> {
    return this.post<Location>('', location);
  }

  async resyncLocation(id: string): Promise<Location> {
    return this.put<Location>(`/${id}/resync`, {});
  }

  async syncTrackman(): Promise<TrackmanSyncResultDto> {
    return this.post<TrackmanSyncResultDto>('/trackman-sync', {});
  }

  async syncTrackmanForLocation(id: string): Promise<TrackmanSyncResultDto> {
    return this.post<TrackmanSyncResultDto>(`/${id}/trackman-sync`, {});
  }

  async getTrackmanCredentials(id: string): Promise<TrackmanCredentialsStatus> {
    return this.get<TrackmanCredentialsStatus>(`/${id}/trackman-credentials`);
  }

  async updateTrackmanCredentials(
    id: string,
    req: UpdateTrackmanCredentialsRequest
  ): Promise<TrackmanCredentialsStatus> {
    return this.put<TrackmanCredentialsStatus>(`/${id}/trackman-credentials`, req);
  }

  async deleteTrackmanCredentials(id: string): Promise<TrackmanCredentialsStatus> {
    return this.delete<TrackmanCredentialsStatus>(`/${id}/trackman-credentials`);
  }

  async getLocationPlans(
    id: string,
    options?: { includeHidden?: boolean }
  ): Promise<Plan[]> {
    if (options?.includeHidden) {
      return this.get<Plan[]>(`/${id}/plans?includeHidden=true`);
    }
    return this.get<Plan[]>(`/${id}/plans`);
  }

  async getLocationCalendars(id: string): Promise<LocationFacilityCalendar[]> {
    return this.get<LocationFacilityCalendar[]>(`/${id}/calendars`);
  }

  async getLocationProducts(id: string): Promise<LocationProductSummary[]> {
    return this.get<LocationProductSummary[]>(`/${id}/products`);
  }

  async getLocationPlanBookingTypes(planId: string): Promise<BookingType[]> {
    return this.get<BookingType[]>(`/${planId}/plan-booking-types`);
  }

  async getLocationBookingTypes(id: string): Promise<BookingType[]> {
    return this.get<BookingType[]>(`/${id}/booking-types`);
  }

  async getSetupStatus(id: string): Promise<LocationSetupStatus> {
    return this.get<LocationSetupStatus>(`/${id}/setup-status`);
  }

  async downloadLocationQrPdf(id: string, locationName?: string): Promise<{ blob: Blob; filename: string }> {
    const response = await api.get(`/location/${encodeURIComponent(id)}/qr-code/pdf`, {
      responseType: 'blob',
    });
    const blob = response.data as Blob;

    // Parse filename from Content-Disposition: attachment; filename="..."
    const disposition =
      (response.headers['content-disposition'] as string | undefined) ??
      (response.headers['Content-Disposition'] as string | undefined) ??
      '';
    const match = /filename\*?=(?:UTF-\d['']*)?"?([^";\r\n]+)"?/i.exec(disposition);
    let filename = match?.[1]?.trim() ?? '';

    filename = filename.replace(/[\x00-\x1f\x7f/\\]/g, '').trim();
    if (!filename) {
      filename = locationName ? `${locationName}'s Shop Code.pdf` : `Shop Code.pdf`;
    }
    if (!/\.pdf$/i.test(filename)) {
      filename = `${filename}.pdf`;
    }

    return { blob, filename };
  }

  async updateLocation(
    locationId: string,
    locationReq: LocationRequest
  ): Promise<Location> {
    return this.put<Location>(`/${locationId}`, locationReq);
  }

  async getPlanPricing(planId: string): Promise<PricingDto> {
    return this.get<PricingDto>(`/${planId}/plan-pricing`);
  }

  async getBookingTypePricing(bookingTypeId: string): Promise<PricingDto> {
    return this.get<PricingDto>(`/${bookingTypeId}/booking-type-pricing`);
  }

  async updatePlan(planId: string, planReq: PriceChangeRequest): Promise<Plan> {
    return this.put<Plan>(`/${planId}/plan-price`, planReq);
  }

  async updateBookingType(
    bookingTypeId: string,
    bookingTypeReq: PriceChangeRequest
  ): Promise<BookingType> {
    return this.put<BookingType>(
      `/${bookingTypeId}/booking-type-price`,
      bookingTypeReq
    );
  }
}

export const locationService = new LocationService();
